import { useEffect, useRef } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { NavArrow } from "@/components/vehicles/NavArrow";

export const Lightbox = ({ open, onOpenChange, images, index, setIndex, title }) => {
  const touchX = useRef(null);
  const n = images.length;
  const go = (d) => setIndex((x) => (x + d + n) % n);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowRight") setIndex((x) => (x + 1) % n);
      if (e.key === "ArrowLeft") setIndex((x) => (x - 1 + n) % n);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, n, setIndex]);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/95 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          data-lenis-prevent
          className="on-dark fixed inset-0 z-50 flex flex-col text-white outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0"
          data-testid="gallery-lightbox"
        >
          <div className="flex items-center justify-between gap-4 px-4 py-3 sm:px-6">
            <DialogPrimitive.Title className="truncate font-display text-base font-bold sm:text-lg">{title}</DialogPrimitive.Title>
            <div className="flex items-center gap-4">
              <span className="text-sm tabular-nums text-zinc-300" data-testid="lightbox-counter">
                {index + 1} / {n}
              </span>
              <DialogPrimitive.Close
                className="flex h-11 w-11 items-center justify-center rounded-md border border-white/25 transition-colors hover:bg-white hover:text-ink"
                aria-label="Close gallery"
                data-testid="lightbox-close-button"
              >
                <X className="h-5 w-5" />
              </DialogPrimitive.Close>
            </div>
          </div>
          <div
            className="relative flex min-h-0 flex-1 items-center justify-center px-2 sm:px-20"
            onTouchStart={(e) => {
              touchX.current = e.touches[0].clientX;
            }}
            onTouchEnd={(e) => {
              const dx = e.changedTouches[0].clientX - (touchX.current ?? 0);
              if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
            }}
          >
            <img src={images[index]} alt={`${title} — photo ${index + 1} of ${n}`} className="max-h-full max-w-full select-none object-contain" data-testid="lightbox-image" />
            <NavArrow dir={-1} onClick={() => go(-1)} className="absolute left-3 top-1/2 -translate-y-1/2 sm:left-6" testId="lightbox-prev-button" />
            <NavArrow dir={1} onClick={() => go(1)} className="absolute right-3 top-1/2 -translate-y-1/2 sm:right-6" testId="lightbox-next-button" />
          </div>
          <div className="flex gap-2 overflow-x-auto px-4 py-4 sm:justify-center sm:px-6">
            {images.map((src, i) => (
              <button
                key={src}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`Show photo ${i + 1}`}
                aria-current={i === index}
                className={cn("aspect-[4/3] w-20 shrink-0 overflow-hidden rounded-sm transition-opacity", i === index ? "ring-2 ring-white" : "opacity-50 hover:opacity-100")}
                data-testid={`lightbox-thumb-${i}`}
              >
                <img src={src} alt="" loading="lazy" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
